import { HttpClient } from '@angular/common/http';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { environment } from '../../../../environments/environment';
import {
    PendingProviderSelectionService,
    ProviderLinkService
} from '../../../core/services';
import { ApiService } from '../../../core/services/api.service';
import {
    AuthenticationResponse,
    CreateUserModel,
    JwtToken,
    User
} from '../models';
import { LocalAccountCreationService } from './local-account-creation.service';

type SessionKey = 'accessToken' | 'user';

/**
 * Older builds cached the OTP security code in sessionStorage, where it survived sign-out
 * and token expiry.  Nothing writes it any more — the code is passed directly to
 * updateUser() instead — but it is removed defensively so a tab that was loaded on a
 * previous build cannot keep a usable code around.  Deliberately not part of SessionKey:
 * widening that type would make the code writable through setSessionItem again.
 */
const LEGACY_SECURITY_CODE_KEY = 'code';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private _user = new BehaviorSubject<User | null>(null);
    private _accountCreated = new ReplaySubject<void>();
    private _signedIn = new ReplaySubject<void>();
    private _tokenExpiration = new Subject<void>();
    private _previousIsSignedIn: boolean | undefined;
    private _handlingSessionExpired = false;

    constructor(
        private readonly _http: HttpClient,
        private readonly _apiService: ApiService,
        private readonly _router: Router,
        private readonly _localAccountCreation: LocalAccountCreationService,
        private readonly _providerLink: ProviderLinkService,
        private readonly _pendingSelection: PendingProviderSelectionService,
        private _googleAnalyticsService: GoogleAnalyticsService,
        @Optional() @SkipSelf() parent?: AuthService
    ) {
        // Make sure that the service is a singleton in the entire application
        if (parent) {
            throw Error(
                `[AuthService]: trying to create multiple instances, but this service should be a singleton`
            );
        }

        this.user$.pipe(filter(Boolean)).subscribe((_) => {
            if (this.cachedClientCode !== runtimeConfig.clientCode) {
                this.handleClientCodeMismatch();
            }
        });

        if (this.isSignedIn) {
            if (this.cachedClientCode !== runtimeConfig.clientCode) {
                this.handleClientCodeMismatch();
                // Must not fall through: handleClientCodeMismatch() signs out, which removes
                // the cached `user`, and the getUser() call below reads `user.id` off it.
                return;
            }

            // I want to get the user from the database right away if I'm signed in.
            // But because transloco is using the HttpClient to get translation files
            // from assets, doing this http call right away messes up with transloco
            // initialization somehow.  I couldn't figure out the root cause, but
            // doing this after things calm down seemed to fix the issue.  Would
            // like to know why this is the case.
            // - JDS 5/22/2021
            setTimeout(() =>
                this.getUser(this.getSessionItem('user').id).subscribe(
                    (user) => {
                        this._user.next(user);
                    }
                )
            );
        } else if (this.accessToken) {
            // Not signed in, but a token is still sitting in sessionStorage: it is expired (or
            // its `user` companion is gone) and can never be used again.  The true -> false
            // transition in isSignedIn cannot cover this case because _previousIsSignedIn
            // starts out undefined, so purge here instead.
            //
            // Nothing is emitted on tokenExpiration$: it is a plain Subject, and
            // AuthPopupService (which depends on this service) has not been constructed yet,
            // so the notification would be dropped.  That matches the existing behaviour of
            // showing no "session expired" snackbar on a fresh page load.
            //
            // Guarded on accessToken so an anonymous page load does no work at all. (This
            // used to also protect in-progress sign-up state, which is no longer persisted —
            // see LocalAccountCreationService.)
            this.endSession();
        }
    }

    get user$() {
        return this._user.asObservable();
    }

    get accountCreated$(): Observable<void> {
        return this._accountCreated.asObservable();
    }

    get successfulSignIn$(): Observable<void> {
        return this._signedIn.asObservable();
    }

    get isSignedIn(): boolean {
        const isTokenExpired = this.isTokenExpired();
        const currentIsSignedIn =
            !!this.accessToken &&
            !!this.getSessionItem('user') &&
            !isTokenExpired;

        if (this._previousIsSignedIn !== currentIsSignedIn) {
            if (
                this._previousIsSignedIn === true &&
                currentIsSignedIn === false &&
                isTokenExpired
            ) {
                // Purge BEFORE notifying, so subscribers can rely on storage already being
                // clean.  The notification path re-enters this getter (AuthPopupService ->
                // transloco -> HttpClient -> AuthInterceptor); with the token already gone
                // isTokenExpired() returns false, so the re-entrant read can neither emit a
                // second time nor hand out a stale bearer token.
                //
                // Keep this inline rather than moving it to a tokenExpiration$ subscription:
                // inline runs unconditionally, independent of DI construction order and of
                // whether an earlier observer throws (RxJS abandons the remaining observers
                // if one does).
                //
                // Storage writes only — no _user.next() and no router navigation.  This getter
                // is read from the always-mounted header/sidenav templates, so it runs during
                // change detection.
                this.endSession();
                this._tokenExpiration.next();
            }
            this._previousIsSignedIn = currentIsSignedIn;
        }

        return currentIsSignedIn;
    }

    get accessToken(): string {
        return this.getSessionItem('accessToken');
    }

    /**
     * Emits once when a previously valid token is found to have expired.
     *
     * Invariant: sensitive sessionStorage data has already been purged by the time this
     * emits, so subscribers must not assume the token or user are still readable.
     */
    get tokenExpiration$(): Observable<void> {
        return this._tokenExpiration.asObservable();
    }

    private get cachedClientCode(): string {
        const cachedClientCode = sessionStorage.getItem('currentClientCode');
        if (cachedClientCode) {
            return cachedClientCode;
        }
        sessionStorage.setItem('currentClientCode', runtimeConfig.clientCode);
        return runtimeConfig.clientCode;
    }

    /**
     * All clients share one origin (the client code is a URL path segment, parsed in
     * app.component.ts), so they also share one sessionStorage namespace.  Loading a second
     * client's portal into a tab that holds a first client's session signs that session out.
     *
     * `cachedClientCode` only ever wrote `currentClientCode` when the key was absent, so it was
     * never updated after a client change and this check stayed true forever — re-signing the
     * user out on every `user$` emission for the rest of the tab's life.  Recording the new code
     * makes the mismatch fire once (LA-22939).
     *
     * ProviderLinkService is reset first so signOut() cannot navigate to
     * `/{newClient}/providers/{previousClientsProviderId}`.
     */
    private handleClientCodeMismatch(): void {
        this._providerLink.reset();
        this.signOut();
        sessionStorage.setItem('currentClientCode', runtimeConfig.clientCode);
    }

    /**
     * Requests a security code from the API.  Use when signing in.
     */
    requestSignInCode(emailOrPhoneNumber: string) {
        return this._apiService.post(`/Users/SendSignInUserCode`, {
            clientId: runtimeConfig.clientId,
            emailOrPhoneNumber: emailOrPhoneNumber
        });
    }

    /**
     * Requests a security code from the API. Use when creating the users account.
     */
    requestCreateAccountCode(emailOrPhoneNumber: string) {
        return this._apiService.post(`/Users/SendCreateAccountCode`, {
            clientId: runtimeConfig.clientId,
            emailOrPhoneNumber: emailOrPhoneNumber
        });
    }

    /**
     * Requests a security code from the API.  Use when updating a user's email or phone number.
     */
    requestUpdateUserCode(userId: number, emailOrPhoneNumber: string) {
        return this._apiService.post(`/Users/SendUpdateUserCode`, {
            userId,
            clientId: runtimeConfig.clientId,
            emailOrPhoneNumber: emailOrPhoneNumber
        });
    }

    /**
     * Sign in with email or phone number and security code received via SMS or email previously.
     */
    signIn(emailOrPhoneNumber: string, securityCode: number) {
        const request = this._http
            .post<AuthenticationResponse>(
                `${environment.apiUrl}/Users/Signin`,
                {
                    clientId: runtimeConfig.clientId,
                    emailOrPhoneNumber: emailOrPhoneNumber,
                    securityCode: securityCode
                }
            )
            .pipe(
                tap((response: AuthenticationResponse) =>
                    this.storeTokenAndUser(response)
                ),
                switchMap((response) => this.getUser(response.userId)),
                tap((user) => {
                    this._user.next(user);
                    this._signedIn.next();

                    this._googleAnalyticsService.login();

                    // redirect everyone that just signed up to the application
                    this._router.navigate([
                        `${runtimeConfig.clientCode}/application`
                    ]);
                })
            );

        return request;
    }

    /**
     * Create account, and if successful, sign-in
     */
    signUp(createUser: CreateUserModel) {
        const request = this._http
            .post<AuthenticationResponse>(
                `${environment.apiUrl}/Users`,
                createUser
            )
            .pipe(
                tap((response: AuthenticationResponse) => {
                    this.storeTokenAndUser(response);
                }),
                switchMap((response) => this.getUser(response.userId)),
                tap((user) => {
                    this._user.next(user);
                    this._signedIn.next();

                    this._googleAnalyticsService.signUp();

                    // A client-level waitlist marker becomes the actual selection now that an
                    // account exists.  Empty counts as absent: a client with no waitlist
                    // providers used to write '', which was copied across and then satisfied the
                    // routing test below — sending the student to the application with no
                    // provider selected at all, past the provider page (LA-22939).
                    this._pendingSelection.promoteWaitlist();

                    // redirect everyone that just signed up to the application
                    if (this._pendingSelection.has())
                        this._router.navigate([
                            `${runtimeConfig.clientCode}/application`
                        ]);
                    else
                        this._router.navigate([
                            `${runtimeConfig.clientCode}/provider`
                        ]);
                })
            );

        return request;
    }

    /**
     * Navigating a provider-linked student back to their provider's page is intended product
     * behaviour — they should not be dropped onto the statewide site.  Do not "fix" this.
     *
     * That navigation re-runs ProviderLinkService.checkForParams, which rewrites
     * `applicationProviderIds` and `applicationSource` from the URL immediately after
     * endSession() cleared them.  That is correct: the tab really is displaying that provider's
     * page, so a student signing in from it should get that provider.  What does not come back
     * is everything the previous student accumulated elsewhere — provider-page multi-selections,
     * site ids, program ids, waitlist ids — which is the contamination LA-22939 is about.
     */
    signOut() {
        this.endSession();
        this._user.next(null);
        if (this._providerLink.hasProvider)
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this._providerLink.providerId}`
            ]);
        else this._router.navigate([`${runtimeConfig.clientCode}/home`]);
    }

    handleSessionExpired() {
        // Guard against multiple concurrent 401s triggering redundant cleanups and navigations
        if (this._handlingSessionExpired) return;
        this._handlingSessionExpired = true;

        this.endSession();
        this._user.next(null);
        this._tokenExpiration.next();
        if (this._providerLink.hasProvider)
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this._providerLink.providerId}`
            ]);
        else this._router.navigate([`${runtimeConfig.clientCode}/home`]);
    }

    getUser(id: number) {
        return this._http.get<User>(`${environment.apiUrl}/Users/${id}`);
    }

    /**
     * @param securityCode a freshly entered code, required only when the user is changing
     * their email or phone number — the API validates it in that case alone.  Pass it in at
     * the point it is collected; never cache it.
     */
    updateUser(user: User, securityCode?: number) {
        const request$ = this._http
            .put<AuthenticationResponse>(
                `${environment.apiUrl}/Users/${user.userId}`,
                {
                    ...user,
                    // UpdateUserModel.SecurityCode is a non-nullable int, so sending null
                    // fails model binding with a 400.  0 is safe: the API ignores the value
                    // entirely unless the email/phone changed, and it can never match a real
                    // code because the known-code bypass is guarded with `knownCode != 0`.
                    securityCode: securityCode ?? 0
                }
            )
            .pipe(
                // The API mints a fresh token on every update, because the identity claims it
                // carries (sub / nameidentifier) are the email or phone number. Discarding it
                // left the session holding a token that names an address the update had just
                // replaced, so the very next request — the getUser below — came back 401.
                tap((response) => this.storeTokenAndUser(response)),
                switchMap((response) => this.getUser(response.userId)),
                tap((user) => this._user.next(user))
            );

        return request$;
    }

    /**
     * Sets accessToken and parses the token to store the email/phone number and user's id.
     */
    private storeTokenAndUser(response: AuthenticationResponse) {
        this.clearSession();

        this.setSessionItem('accessToken', response.accessToken);
        const token = this.getParsedJwt(response.accessToken);
        this.setSessionItem('user', {
            emailOrPhone:
                token?.[
                    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
                ],
            id: response.userId
        });
    }

    private clearSession() {
        // remove authentication related data from session
        this.removeSessionItem('accessToken');
        this.removeSessionItem('user');
        sessionStorage.removeItem(LEGACY_SECURITY_CODE_KEY);
    }

    /**
     * Removes everything that must not outlive the session:
     *  - the access token, the cached user and the legacy OTP key (clearSession)
     *  - the in-progress account-creation values, which include the sign-up security code and
     *    the student's name and contact details (LocalAccountCreationService)
     *  - the staged provider/program selection (LA-22939).  It is a per-tab sessionStorage
     *    global, so without this a second student signing in on the same device inherited the
     *    first student's choices.  A genuine deep-link visit re-seeds it from the URL via
     *    ProviderLinkService.checkForParams, so clearing here does not break that flow — see
     *    the note on signOut().
     *
     * Deliberately does NOT touch:
     *  - `selectedLanguage` — read by CurrentCultureInterceptor on every request and by the
     *    header/sidenav to set the active transloco language; clearing it would silently
     *    revert a Spanish-speaking user to English.
     *  - `currentClientCode` — the tenant-mismatch check in the constructor depends on it.
     */
    private endSession() {
        this.clearSession();
        this._localAccountCreation.clear();
        this._pendingSelection.clearAll();
    }

    private setSessionItem(name: SessionKey, item: any) {
        sessionStorage.setItem(name, JSON.stringify(item));
    }

    private getSessionItem(name: SessionKey) {
        const item = sessionStorage.getItem(name);
        if (item) return JSON.parse(item);
        else return null;
    }

    private removeSessionItem(name: SessionKey) {
        sessionStorage.removeItem(name);
    }

    /**
     * Returns a JS object representation of a Javascript Web Token from its common encoded string form.
     *
     * Borrowed from https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library#answer-65248576
     * @param token a Javascript Web Token in base64 encoded, '.' separated form
     * @returns an object-representation of the token
     */
    private getParsedJwt(token: string): JwtToken | null {
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (error) {
            // TODO: LOG ME
            return null;
        }
    }

    private isTokenExpired(): boolean {
        const token = this.accessToken;
        if (token) {
            const parsed = this.getParsedJwt(token);
            if (parsed) {
                return Date.now() >= parsed.exp * 1000;
            }
        }
        return false;
    }
}
